/**
 * El idioma del jugador: cual se usa, y cuando llega.
 *
 * ---------------------------------------------------------------------------
 * POR QUE ESTO YA NO ES UN SOLO FICHERO (25-oct-2026)
 * ---------------------------------------------------------------------------
 * Los cuatro idiomas vivian juntos aqui y entraban enteros en la PRIMERA
 * CARGA, asi que cada jugador descargaba y parseaba tres idiomas que no iba a
 * leer nunca. Con la primera carga en 688,7 de 700 KB crudos, esos tres
 * idiomas eran casi todo el margen que quedaba.
 *
 * Ahora hay un fichero por idioma y solo el espanol entra de serie:
 *
 *   es   estatico, siempre presente
 *   en   \
 *   pt    > un `import()` cada uno, un trozo cada uno
 *   ru   /
 *
 * **El espanol se queda dentro a proposito, y no es por ser el primero de la
 * lista:** es el idioma al que cae todo lo que falte. Si tambien se cargara
 * por detras, el respaldo podria no haber llegado todavia en el momento en que
 * hace falta, y no hay respaldo del respaldo. Teniendolo estatico, `td()`
 * nunca se queda sin algo que devolver.
 *
 * ---------------------------------------------------------------------------
 * LO QUE ESTO CAMBIA DE COMPORTAMIENTO
 * ---------------------------------------------------------------------------
 * `fijarIdioma` era sincrona y ahora devuelve una promesa, porque para un
 * jugador en ingles, portugues o ruso hay que ir a buscar su fichero. La
 * alternativa era pintar en espanol y cambiar los textos al llegar, y se
 * descarto: `arrancar()` ya era `async` y puede esperar **antes del primer
 * pintado**, asi que se cambia un parpadeo de idioma por unos milisegundos de
 * espera en un fichero de 5 KB del mismo dominio. Para el publico principal
 * —LatAm, en espanol— no hay ni espera ni peticion.
 *
 * Y si ese fichero no llega (un tunel, un 404 tras un despliegue a medias), se
 * sigue en espanol y el juego se abre igual. Perder un idioma es molesto; no
 * dejar entrar a quien acaba de abrir el juego es mucho peor.
 */

import { sueltos as esSueltos, textos as esTextos } from './es';
import { codigoActual as leerCodigo, fijarCodigo } from './numero';
import type { Idioma, Textos } from './tipos';

export type { Idioma, Textos } from './tipos';
// `numero` se reexporta desde aqui para que quien lo usa siga pidiendoselo a
// `./i18n` y no tenga que saber que el estado del idioma vive en otro fichero.
export { numero } from './numero';

/** Los idiomas que existen. El orden no significa nada. */
const TRADUCIDOS: readonly string[] = ['es', 'en', 'pt', 'ru'];

let actual: Textos = esTextos;
let sueltosActual: Record<string, string> = esSueltos;

/**
 * Trae el fichero de un idioma.
 *
 * Es un `switch` con rutas literales y no un `import(\`./${codigo}\`)` porque
 * vite necesita ver la ruta escrita para saber que trozos tiene que crear. Con
 * la ruta calculada mete los cuatro idiomas en un trozo comodin y el reparto
 * no sirve de nada: se cargarian todos igual, solo que mas tarde.
 */
function cargar(codigo: Idioma): Promise<{ textos: Textos; sueltos: Record<string, string> }> {
  switch (codigo) {
    case 'en':
      return import('./en');
    case 'pt':
      return import('./pt');
    case 'ru':
      return import('./ru');
    default:
      return Promise.resolve({ textos: esTextos, sueltos: esSueltos });
  }
}

/** Deja puesto un idioma que YA esta cargado. Un solo sitio toca el estado. */
function aplicar(codigo: Idioma, textos: Textos, suelt: Record<string, string>): void {
  fijarCodigo(codigo);
  actual = textos;
  sueltosActual = suelt;
  // Se pone al aplicar y no antes: el `lang` del documento no debe anunciar un
  // idioma cuyos textos todavia no estan.
  //
  // Y se pregunta si existe `document` porque este modulo son TEXTOS, y los
  // textos se prueban en un test de verdad: el proyecto no monta un DOM para
  // los tests, asi que sin esta guarda no se podria comprobar que el respaldo
  // al espanol funciona, que es lo unico que no puede fallar aqui.
  if (typeof document !== 'undefined') {
    document.documentElement.lang = codigo;
  }
}

/**
 * Fija el idioma a partir del `language_code` de Telegram.
 *
 * Se llama UNA vez, en `arrancar()`, antes del primer pintado. Si el idioma no
 * esta traducido se queda el espanol: es el publico principal, y un texto en
 * espanol se entiende mejor que una clave sin traducir.
 */
export async function fijarIdioma(codigo: string | undefined): Promise<Idioma> {
  const corto = (codigo ?? 'es').slice(0, 2).toLowerCase();
  const elegido = (TRADUCIDOS.includes(corto) ? corto : 'es') as Idioma;

  if (elegido === 'es') {
    aplicar('es', esTextos, esSueltos);
    return 'es';
  }

  try {
    const modulo = await cargar(elegido);
    aplicar(elegido, modulo.textos, modulo.sueltos);
    return elegido;
  } catch (err) {
    console.warn('no pude cargar el idioma', elegido, '- sigo en espanol', err);
    aplicar('es', esTextos, esSueltos);
    return 'es';
  }
}

/** Los textos del idioma activo. */
export function t(): Textos {
  return actual;
}

export function idiomaActual(): Idioma {
  return leerCodigo();
}

/**
 * Un texto suelto. Cae al espanol si el idioma no lo tiene.
 *
 * Se llama `td` desde que estos textos eran solo los de los duelos, y se queda
 * asi: lo llaman casi cien sitios y renombrarlo seria un diff enorme que nadie
 * va a leer. Lee «texto suelto».
 *
 * El respaldo es `esSueltos`, que esta importado de forma estatica: por eso
 * funciona siempre y no depende de que haya llegado ningun `import()`.
 */
export function td(clave: string): string {
  return sueltosActual[clave] ?? esSueltos[clave] ?? clave;
}
